import L from "leaflet"

import { LocationPointJson } from "./models/location-point"
import { ReportPointJson } from "./models/report-point"
import { ShopPointJson } from "./models/shop-point"

interface PointsOfInterest {
  locations: LocationPointJson[]
  reports: ReportPointJson[]
  shops: ShopPointJson[]
}

export type POIType = "locations" | "metar_reports" | "shops"

export class LeafletRepository {
  private loadingQueue: Promise<any>[] = []
  private readonly elevation: string
  private readonly memberMode: boolean
  private readonly onLoadStart: () => void
  private readonly onLoadEnd: () => void

  constructor(
    elevation: string,
    memberMode: boolean,
    onLoadStart: () => void,
    onLoadEnd: () => void,
  ) {
    this.elevation = elevation
    this.memberMode = memberMode
    this.onLoadStart = onLoadStart
    this.onLoadEnd = onLoadEnd
  }

  constructLocationUri(filename: string) {
    if (this.elevation) {
      return `/resorts/${filename}/${this.memberMode ? "16-day" : "6day"}/${this.elevation}`
    }
    return `/resorts/${filename}`
  }

  async loadPointsOfInterest(
    bounds: L.LatLngBounds,
    pointTypes: POIType[],
  ): Promise<PointsOfInterest> {
    const normalizedSW = bounds.getSouthWest().wrap()
    const normalizedNE = bounds.getNorthEast().wrap()
    const normalizedBounds = L.latLngBounds(normalizedSW, normalizedNE)
    const bbox = normalizedBounds.toBBoxString()
    const url = `/osm/points_of_interest.json?types=${pointTypes.join(",")}&bbox=${bbox}`

    return this.fetch<PointsOfInterest>(url)
  }

  async loadAllPlaces(): Promise<Array<[number, number]>> {
    return this.fetch<Array<[number, number]>>("/osm/coordinates.json")
  }

  private async fetch<T>(url: string): Promise<T> {
    const loader = fetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`${response.status}: ${response.statusText}`)
        }

        try {
          return (await response.json()) as T
        } catch (error) {
          throw new Error(`Failed parsing json, "${error}"`)
        }
      })
      .finally(() => {
        this.removeFromQueue(loader)
      })

    this.addToQueue(loader)
    return loader
  }

  private removeFromQueue(loader: Promise<any>): void {
    const index = this.loadingQueue.indexOf(loader)
    if (index !== -1) {
      this.loadingQueue.splice(index, 1)
    }

    if (this.loadingQueue.length === 0) {
      this.onLoadEnd()
    }
  }

  private addToQueue(loader: Promise<any>): void {
    this.loadingQueue.push(loader)

    if (this.loadingQueue.length === 1) {
      this.onLoadStart()
    }
  }
}
